// Converts logged PT compliance (pt_session events) into synthetic
// GymExercisePayloads so physical-therapy activity feeds the same Trainer ->
// Balance muscle-load math that gym logs do (PT_MUSCLE_BALANCE_PLAN.md Phase 3,
// D-PTM-4/8/9/10/12).
//
// This module only SYNTHESIZES payloads — it never changes computeMuscleLoad
// or computeAnatomicalLoad. Folding PT special-casing into those functions
// would silently change every existing chart (same invariant
// shared/src/muscleMap.ts:1-8 states for the anatomical layer).

import type { MedicalEvent } from "./types.js";
import type { GymExercisePayload, MuscleGroup } from "./gym.js";
import type { AnatomicalMuscle } from "./muscleMap.js";
import type { PtProgramPayload, PtSessionPayload } from "./physicalTherapy.js";
import { getPtExerciseById } from "./ptExerciseCatalog.js";
import { localCalendarDate } from "./datetime.js";
import type { ActivityPurpose } from "./trainingLoad.js";

export interface PtDerivedGymPayload extends GymExercisePayload {
  /** Always true — marks this payload as synthesized from a pt_session, not a real
   *  gym_exercise event. Used by the Balance logs modal to badge the row. */
  isPtDerived: true;
  ptProgramEventId: string;
  ptExerciseId: string;
  /** Denormalized from the resolved PtExercise's purpose (TRAINER_ACCURACY_STAGE_1_2_PLAN
   *  D-TA-1/F2, Task 2.5). Absent for scanned/custom exercises, which carry no purpose
   *  tag; computeTrainingLoad's default resolution applies to those. */
  purpose?: ActivityPurpose;
  /** True for every PT-derived row: laterality is genuinely UNKNOWN — never logged —
   *  not measured-bilateral. `side` stays absent so computeTrainingLoad routes the
   *  dose to both sides (matching a real "both" gym log), but this flag is v2's marker
   *  that the absence must not be read as an explicit "both" the way a real bilateral
   *  gym log's absence would be. */
  ptLateralityUnknown: true;
}

export function isPtDerivedPayload(p: GymExercisePayload): p is PtDerivedGymPayload {
  return (p as Partial<PtDerivedGymPayload>).isPtDerived === true;
}

/** A program exercise (catalog or scanned) that has been muscle-tagged and is
 *  therefore eligible to contribute Balance load (D-PTM-10). */
interface MuscleTagged {
  id: string;
  name: string;
  primaryMuscles: MuscleGroup[];
  secondaryMuscles: MuscleGroup[];
  primaryMuscleIds?: AnatomicalMuscle[];
  secondaryMuscleIds?: AnatomicalMuscle[];
  /** From the curated PtExercise only — CustomPtExercise (scanned/AI-authored)
   *  carries no purpose tag today, so this stays undefined for those. */
  purpose?: ActivityPurpose;
}

function buildExerciseById(program: PtProgramPayload): Map<string, MuscleTagged> {
  const map = new Map<string, MuscleTagged>();
  for (const id of program.exerciseIds ?? []) {
    const catalogEx = getPtExerciseById(id);
    if (catalogEx && catalogEx.primaryMuscles.length > 0) {
      map.set(id, catalogEx);
    }
  }
  // An id present in both the curated catalog and customExercises wins from
  // customExercises (scanned content is the more specific/current source).
  for (const ex of program.customExercises ?? []) {
    if (ex.primaryMuscles && ex.primaryMuscles.length > 0) {
      map.set(ex.id, {
        id: ex.id,
        name: ex.name,
        primaryMuscles: ex.primaryMuscles,
        secondaryMuscles: ex.secondaryMuscles ?? [],
        primaryMuscleIds: ex.primaryMuscleIds,
        secondaryMuscleIds: ex.secondaryMuscleIds,
      });
    }
  }
  return map;
}

/**
 * Converts pt_program + pt_session events into synthetic GymExercisePayloads
 * that computeMuscleLoad / computeAnatomicalLoad can consume unchanged.
 * Untagged exercises, skipped sessions, and deleted events contribute nothing.
 *
 * `timezone` resolves the dedupe key's calendar day (TRAINER_ACCURACY_STAGE_1_2_PLAN
 * F14/Task 2.5) — pass the caller's patient-local zone, matching every other Trainer
 * surface's `localCalendarDate` usage.
 */
export function ptSessionsToGymPayloads(
  programEvents: MedicalEvent[],
  sessionEvents: MedicalEvent[],
  timezone: string,
): PtDerivedGymPayload[] {
  const programs = new Map<string, PtProgramPayload>();
  for (const e of programEvents) {
    if (e.deleted) continue;
    programs.set(e.eventId, e.payload as unknown as PtProgramPayload);
  }

  const exercisesByProgram = new Map<string, Map<string, MuscleTagged>>();
  for (const [eventId, program] of programs) {
    exercisesByProgram.set(eventId, buildExerciseById(program));
  }

  // Newest-completion-wins per (program, exercise, patient-local day) key.
  // Deduping to the first-encountered session (the old behaviour) could
  // discard a newer in-window completion in favour of an older out-of-window
  // one; iterating every session and keeping the latest `performedAt` per key
  // fixes that regardless of the input array's order.
  const byKey = new Map<string, { target: MuscleTagged; performedAt: string; programEventId: string }>();

  for (const e of sessionEvents) {
    if (e.deleted) continue;
    const p = e.payload as unknown as PtSessionPayload;
    if (p.skipped) continue;
    if (!programs.has(p.programEventId)) continue;

    const performedAt = p.performedAt || e.occurredAt;
    if (Number.isNaN(Date.parse(performedAt))) continue;

    const exerciseById = exercisesByProgram.get(p.programEventId)!;
    // Whole-program completion (no exerciseId) fans out to every muscle-tagged
    // exercise in the program. This assumes every listed exercise was actually
    // done that day, which is a known simplification — per-exercise completion
    // tracking is a Stage 3 improvement (F14, PT half).
    const targets: MuscleTagged[] = p.exerciseId
      ? exerciseById.has(p.exerciseId)
        ? [exerciseById.get(p.exerciseId)!]
        : []
      : [...exerciseById.values()];

    for (const target of targets) {
      const dayKey = localCalendarDate(performedAt, timezone);
      const key = `${p.programEventId}|${target.id}|${dayKey}`;
      const existing = byKey.get(key);
      if (!existing || Date.parse(performedAt) > Date.parse(existing.performedAt)) {
        byKey.set(key, { target, performedAt, programEventId: p.programEventId });
      }
    }
  }

  const results: PtDerivedGymPayload[] = [];
  for (const { target, performedAt, programEventId } of byKey.values()) {
    results.push({
      isPtDerived: true,
      ptProgramEventId: programEventId,
      ptExerciseId: target.id,
      userExerciseId: `pt:${programEventId}:${target.id}`,
      name: target.name,
      performedAt,
      primaryMuscles: target.primaryMuscles,
      secondaryMuscles: target.secondaryMuscles,
      primaryMuscleIds: target.primaryMuscleIds,
      secondaryMuscleIds: target.secondaryMuscleIds,
      purpose: target.purpose,
      // Laterality unknown, not measured-bilateral — see PtDerivedGymPayload doc.
      // `side` stays absent/undefined so routing still treats it as both sides.
      ptLateralityUnknown: true,
    });
  }

  return results;
}
